import { memo, useState } from 'react';
import type { AgentConnection } from '@cef-ai/vault-sdk';
import type { Signer } from '@cef-ai/signer';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { AgentAvatar, Card, Chip, IconButton, Menu, MenuItem } from '@cere/cere-design-system';
import { displayAgentName, displayPublisher } from '@/lib/vault/agent-display';
import { marketplaceAgentStoreUrl } from '@/lib/marketplace/links';
import { useConfig } from '@/src/config/context';
import { agentAnchor } from '@/src/data/anchors';
import type { AgentCard } from '@/src/data/agent-cards';
import type { Widget } from '@/lib/manifest/types';
import { CreditChip } from '@/src/components/CreditChip';
import { FirstTimeOnboard } from '@/src/components/FirstTimeOnboard';
import { StatusDot } from '@/src/components/StatusDot';
import { WidgetTabs } from '@/src/components/WidgetTabs';

interface Props {
  agent: AgentConnection;
  card?: AgentCard;
  /** Widget signer (the Cere wallet's signingSigner) — bridged into iframes. */
  signer: Signer | null;
  scopeLabel?: string;
  /** When provided, an actions menu (⋮) with a Disconnect item is rendered.
   *  Takes the agent so the parent can pass a stable reference (a fresh
   *  per-agent closure here would defeat this component's memo). */
  onDisconnect?: (agent: AgentConnection) => void;
  onSelectAnchor: (anchor: string) => void;
}

function isFirstTime(card: AgentCard | undefined, agent: AgentConnection): boolean {
  if (card?.firstTime) return true;
  const scope = (agent.scope || '').toLowerCase();
  return scope === 'pending' || scope === 'not-set-up' || scope === 'unconfigured';
}

// Memoized: the ~10s vault-wide poll re-renders the home tree, but an agent's
// props (agent/card/signer/scopeLabel) are stable across polls, so each block
// (and its live widget iframe) skips re-render.
export const AgentBlock = memo(function AgentBlock({ agent, card, signer, scopeLabel, onDisconnect, onSelectAnchor }: Props) {
  const { marketplaceBaseUrl, ddcCdnUrl } = useConfig();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  // Widgets are read from the agent's DDC bucket manifest (see
  // useAgentsAndCards → fetchAgentWidgets) and hung on the card. The card-only
  // marketplace endpoint never returns them.
  const widgets: Widget[] = card?.widgets ?? [];
  const name = card?.name ?? displayAgentName(agent.agentId);
  const firstTime = isFirstTime(card, agent);
  const storeUrl = marketplaceAgentStoreUrl(marketplaceBaseUrl, agent.agentId);

  return (
    <Card variant="outlined" data-anchor={agentAnchor(agent.agentId)} className="vault-block vault-aw">
      <div className="vault-aw__head">
        <a
          className="vault-aw__avatar-link"
          href={storeUrl}
          target="_blank"
          rel="noreferrer"
          aria-label={`View ${name} on the marketplace`}
        >
          <AgentAvatar seed={agent.agentId} name={name} size={40} shadow iconUrl={card?.iconUrl} />
        </a>
        <div className="vault-aw__head-text">
          <div className="vault-aw__title-row">
            <a className="vault-aw__name-link" href={storeUrl} target="_blank" rel="noreferrer">
              <h3 className="vault-aw__name">{name}</h3>
            </a>
            <StatusDot status={agent.status} />
            {scopeLabel && <Chip variant="default" size="small" label={scopeLabel} />}
            {firstTime && <Chip variant="warning" size="small" label="Not set up" />}
            <CreditChip agentId={agent.agentId} agentName={name} onSelectAnchor={onSelectAnchor} />
          </div>
          {card?.description && <div className="vault-aw__desc">{card.description}</div>}
          <div className="vault-aw__meta">
            <span>By</span>
            <span className="vault-aw__publisher">{displayPublisher(agent.agentId)}</span>
            <span className="vault-aw__sep">·</span>
            <span className="vault-aw__publisher">v{agent.version}</span>
          </div>
        </div>
        {onDisconnect && (
          <div className="vault-aw__actions">
            <IconButton
              aria-label="Agent actions"
              size="small"
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => setMenuAnchor(e.currentTarget)}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
            <Menu anchorEl={menuAnchor} onClose={() => setMenuAnchor(null)}>
              <MenuItem label="Disconnect" onClick={() => { setMenuAnchor(null); onDisconnect(agent); }} />
            </Menu>
          </div>
        )}
      </div>

      <div className="vault-aw__body">
        {firstTime ? (
          <FirstTimeOnboard agentName={name} />
        ) : widgets.length > 0 ? (
          <WidgetTabs widgets={widgets} signer={signer} cdnBaseUrl={ddcCdnUrl} />
        ) : (
          <div className="vault-aw__empty"><span>No widgets from {name} yet.</span></div>
        )}
      </div>
    </Card>
  );
});
